import { Button, DetailField, PageBreadcrumb, Spinner, Tag, Tooltip } from "@/modules/common/components";
import useNavigateBack from "@/modules/common/hooks/useNavigateBack";
import { ADMIN_USER_ROLE_OPTIONS, ADMIN_USER_STATUS_OPTIONS, AdminUserRole, AdminUserStatus } from "@/modules/common/types/Users.enum";
import { formatDate, formatValue } from "@/modules/common/utils/format";
import { ROUTES } from "@/routes/constants/routes";
import { generatePath, useParams } from "react-router-dom";
import useUserDetails from "./hooks/useUserDetails";
import useToastOnError from "@/modules/common/hooks/useToastOnError";
import { ArrowLeft, PencilLineIcon, SendIcon } from "lucide-react";
import { useSessionStore } from "@/modules/auth/store/useSessionStore";
import useSendFirstAccessCode from "@/modules/auth/hooks/useSendFirstAccessCode";
import useResendCooldown from "@/modules/common/hooks/useResendCooldown";

const RESEND_COOLDOWN_SECONDS = 30;

const UserDetails = () => {
  const { id } = useParams();
  const { canUserSee } = useSessionStore();
  const navigateBack = useNavigateBack(ROUTES.USERS.LIST);
  const sendFirstAccessCode = useSendFirstAccessCode();
  const { cooldown: resendCooldown, start: startResendCooldown } = useResendCooldown(RESEND_COOLDOWN_SECONDS);

  const { data: user, isLoading, isError, error } = useUserDetails({ id: id!, enabled: !!id });

  useToastOnError(isError, error, "Erro ao carregar usuário.", navigateBack);

  const pendingFirstAccess = !user?.lastLoginAt;
  const isAdmin = user?.role === AdminUserRole.ADMIN;
  const isActive = user?.status === AdminUserStatus.ACTIVE;
  const canResendAccessCode = canUserSee(AdminUserRole.ADMIN) && pendingFirstAccess && isActive;
  const statusLabel = ADMIN_USER_STATUS_OPTIONS.find((option) => option.value === String(user?.status))?.label ?? "-";
  const roleLabel = ADMIN_USER_ROLE_OPTIONS.find((option) => option.value === String(user?.role))?.label ?? "-";

  const onCodeSend = () => {
    if (!user || !canResendAccessCode) return;
    if (resendCooldown > 0 || sendFirstAccessCode.isPending) return;

    sendFirstAccessCode.mutate(
      { email: user.email },
      {
        onSuccess: () => {
          startResendCooldown();
        },
      },
    );
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <PageBreadcrumb items={[{ label: "Usuários Administrativos", to: ROUTES.USERS.LIST }, ...(user ? [{ label: user.name }] : [])]} />
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : !!user ? (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col">
              <Tooltip label={user.name}>
                <p className="text-[22px] font-bold text-ui-primary truncate md:line-clamp-2 md:whitespace-normal">{user.name}</p>
              </Tooltip>
              <p className="h-5 text-[14px] text-ui-text-muted">{user.email}</p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button variant="outlined" label="Voltar" icon={<ArrowLeft />} className="w-full sm:w-auto" onClick={navigateBack} />
              {canUserSee(AdminUserRole.ADMIN) && (
                <Button
                  label="Editar Usuário"
                  route={generatePath(ROUTES.USERS.EDIT, { id: user.id })}
                  icon={<PencilLineIcon />}
                  className="w-full sm:w-auto"
                />
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 rounded-xl bg-white p-4">
            <div className="flex flex-col">
              <p className="text-[16px] font-bold text-ui-primary">Dados Gerais</p>
              <p className="text-[12px] text-ui-text-muted">Informações cadastrais do usuário.</p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <DetailField label="Nome">{user.name}</DetailField>
              <DetailField label="E-mail">{user.email}</DetailField>

              <DetailField label="Telefone">{user.phoneNumber ? formatValue("phoneNumber", user.phoneNumber) : "-"}</DetailField>
              <DetailField label="Perfil">
                <Tag label={roleLabel} variant={isAdmin ? "primary" : "neutral"} className="border-0 py-0" />
              </DetailField>

              <DetailField label="Status">
                <Tag label={statusLabel} variant={isActive ? "success" : "neutral"} className="border-0 py-0" />
              </DetailField>
              <DetailField label="Último acesso">
                {user.lastLoginAt ? formatDate(user.lastLoginAt, "DD/MM/YYYY [às] HH:mm") : null}
              </DetailField>

              <DetailField label="Editado por">{user.editedBy?.name}</DetailField>
              <DetailField label="Criado em">{formatDate(user.createdAt, "DD/MM/YYYY [às] HH:mm")}</DetailField>
            </div>
          </div>

          {canResendAccessCode && (
            <div className="flex flex-col gap-3 rounded-xl bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col">
                <p className="text-[16px] font-bold text-ui-primary">Ações do usuário</p>
                <p className="text-[12px] text-ui-text-muted">Envie um novo código de acesso para o usuário realizar o primeiro acesso.</p>
              </div>

              <Button
                variant="outlined"
                color="success"
                className="w-full font-semibold disabled:cursor-not-allowed sm:w-auto"
                label={resendCooldown > 0 ? `Reenviar código em ${resendCooldown}s` : "Reenviar código de acesso"}
                icon={<SendIcon size={18} />}
                disabled={resendCooldown > 0 || sendFirstAccessCode.isPending}
                loading={sendFirstAccessCode.isPending}
                onClick={onCodeSend}
              />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
};

export default UserDetails;
